import { t } from '../i18n';
import { useAuthStore } from '../store/authStore';
import { useServerHealthStore } from '../store/serverHealthStore';
import { normalizeServerUrl } from '../utils/serverKey';
import type {
  ApiResponse,
  SignedRequest,
  RegisterIdentityPayload,
  RegisterDevicePayload,
  SetVaultPayload,
  GetVaultResponse,
  GetIceServersResponse,
  GetTurnCredentialsResponse,
  ServerCapabilitiesResponse,
  IdentityRecord,
  DeviceRecord,
  DeviceRegistrationAttestation,
  RevokeDevicePayload,
  PublicKey,
  EncryptedBlob,
  CreateDeviceEnrollmentRequest,
  CreateDeviceEnrollmentResponse,
  ReadDeviceEnrollmentRequest,
  ReadDeviceEnrollmentResponse,
  CompleteDeviceEnrollmentRequest,
  CompleteDeviceEnrollmentResponse,
  ReadDeviceEnrollmentPayloadResponse,
  CreateTemporaryTrustedAccessRequestRequest,
  CreateTemporaryTrustedAccessRequestResponse,
  ListTemporaryTrustedAccessRequestsResponse,
  ApproveTemporaryTrustedAccessRequestRequest,
  RejectTemporaryTrustedAccessRequestRequest,
  ReadTemporaryTrustedAccessPayloadResponse
} from '@shared/types';

/**
 * API Client with configurable base URL
 */
class APIClient {
  private baseUrl: string = '';

  private resolveHealthKey(baseUrl: string): string | null {
    const normalizedBase = normalizeServerUrl(baseUrl);
    if (!normalizedBase) return null;

    const identities = Object.values(useAuthStore.getState().identities);
    const matchingIdentity = identities.find((identity) => {
      const identityBase = normalizeServerUrl(identity.serverUrl || identity.serverId);
      return identityBase === normalizedBase;
    });

    return matchingIdentity?.serverId || normalizedBase;
  }

  private async parseResponseBody(response: Response): Promise<{ json: unknown | null; text: string }> {
    const text = await response.text();
    const contentType = response.headers.get('content-type') || '';

    const looksLikeJson = contentType.includes('application/json') || /^\s*[[{]/.test(text);
    if (!looksLikeJson) {
      return { json: null, text };
    }

    try {
      return { json: JSON.parse(text) as unknown, text };
    } catch {
      return { json: null, text };
    }
  }

  /**
   * Set base URL for API requests
   */
  setBaseUrl(url: string): void {
    // Normalize URL - remove trailing slash.
    // IMPORTANT: if the user enters a bare host (e.g. "test.call2.you"),
    // treat it as https://... . Otherwise fetch() would interpret it as a
    // relative path and produce URLs like https://client.host/test.call2.you/api/...
    let normalized = (url || '').trim();
    if (!normalized) {
      this.baseUrl = '';
      return;
    }

    // IMPORTANT: this app does NOT use a same-origin proxy.
    // API calls must never go to the client host, so relative base URLs are forbidden.
    if (normalized.startsWith('/')) {
      throw new Error('Relative API base URL is not allowed. Use an absolute server URL like https://example.com');
    }

    this.baseUrl = normalizeServerUrl(normalized) || normalized.replace(/\/$/, '');
  }

  /**
   * Get current base URL
   */
  getBaseUrl(): string {
    // IMPORTANT: API server is never the client host.
    // baseUrl MUST be an absolute URL (https://...).
    return this.baseUrl;
  }

  /**
   * Make API request
   */
  async apiRequest<T>(
    endpoint: string,
    options?: RequestInit,
    baseUrlOverride?: string
  ): Promise<ApiResponse<T>> {
    const base = (baseUrlOverride ? baseUrlOverride.replace(/\/$/, '') : this.getBaseUrl());
    if (!base) {
      throw new APIError('API base URL is not configured (refusing to call client origin)', {
        status: 0,
        url: '',
        endpoint,
        responseJson: null,
        responseText: ''
      });
    }
    const url = `${base}/api${endpoint}`;

    let response: Response;
    try {
      response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers
        }
      });
    } catch (error) {
      const healthKey = this.resolveHealthKey(base);
      if (healthKey) {
        useServerHealthStore.getState().markSyncFailure(healthKey, error instanceof Error ? error.message : '');
      }
      throw new APIError(t('common.circleConnectionFailed'), {
        status: 0,
        url,
        endpoint,
        responseJson: null,
        responseText: error instanceof Error ? error.message : ''
      });
    }

    const { json, text } = await this.parseResponseBody(response);
    const healthKey = this.resolveHealthKey(base);

    if (!response.ok) {
      if (healthKey) {
        useServerHealthStore.getState().markSyncFailure(
          healthKey,
          typeof text === 'string' && text.trim() ? text : `HTTP ${response.status}`
        );
      }
      const errorMessage =
        response.status >= 500
          ? t('common.circleConnectionFailed')
          : (json && typeof json === 'object' && json !== null && 'error' in json
            ? (json as any).error?.message
            : null) ||
          `API request failed (${response.status})`;

      throw new APIError(errorMessage, {
        status: response.status,
        url,
        endpoint,
        responseJson: json,
        responseText: text
      });
    }

    if (healthKey) {
      useServerHealthStore.getState().markSyncSuccess(healthKey);
    }

    if (!json) {
      throw new APIError(t('common.circleConnectionFailed'), {
        status: response.status,
        url,
        endpoint,
        responseJson: null,
        responseText: text
      });
    }

    return json as ApiResponse<T>;
  }

  /**
   * Make signed API request
   */
  async signedRequest<T, P>(
    endpoint: string,
    signedPayload: SignedRequest<P>,
    baseUrlOverride?: string
  ): Promise<ApiResponse<T>> {
    return this.apiRequest<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(signedPayload)
    }, baseUrlOverride);
  }

}

export class APIError extends Error {
  status: number;
  url: string;
  endpoint: string;
  responseJson: unknown | null;
  responseText: string;

  constructor(
    message: string,
    details: {
      status: number;
      url: string;
      endpoint: string;
      responseJson: unknown | null;
      responseText: string;
    }
  ) {
    super(message);
    this.name = 'APIError';
    this.status = details.status;
    this.url = details.url;
    this.endpoint = details.endpoint;
    this.responseJson = details.responseJson;
    this.responseText = details.responseText;
  }
}

// Export singleton instance
export const apiClient = new APIClient();

/**
 * Auth API
 */
export const authAPI = {
  /**
   * Get encrypted identity private key by public key (for device recovery)
   */
  getEncryptedIdentityKey: async (publicKey: PublicKey, serverUrl?: string): Promise<ApiResponse<{
    identityId: string;
    publicKey: PublicKey;
    encryptedPrivateKey: EncryptedBlob;
    role?: string;
  }>> => {
    return apiClient.apiRequest('/auth/identity/encrypted-key', {
      method: 'POST',
      body: JSON.stringify({ publicKey })
    }, serverUrl);
  },

  /**
   * Check if invite is valid
   */
  checkInvite: async (token: string, serverUrl?: string): Promise<ApiResponse<{ valid: boolean; serverName?: string; noNamesOnServer?: boolean; expiresAt: string; remainingUses: number }>> => {
    return apiClient.apiRequest('/auth/check-invite', {
      method: 'POST',
      body: JSON.stringify({ token })
    }, serverUrl);
  },

  /**
   * Register new identity
   */
  register: async (
    payload: RegisterIdentityPayload,
    serverUrl?: string
  ): Promise<ApiResponse<{ identity: IdentityRecord }>> => {
    return apiClient.apiRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload)
    }, serverUrl);
  },

  registerOwner: async (
    payload: import('@shared/types').RegisterOwnerIdentityPayload,
    serverUrl?: string
  ): Promise<ApiResponse<{
    identity: IdentityRecord;
    serverAdmin: {
      serverAdminId: string;
      identityId: string;
      grantedAt: string;
    } | null;
  }>> => {
    return apiClient.apiRequest('/auth/register-owner', {
      method: 'POST',
      body: JSON.stringify(payload)
    }, serverUrl);
  },

  /**
   * Register device
   */
  registerDevice: async (
    signedPayload: SignedRequest<RegisterDevicePayload>,
    serverUrl?: string
  ): Promise<ApiResponse<DeviceRecord>> => {
    return apiClient.apiRequest('/auth/register-device', {
      method: 'POST',
      body: JSON.stringify(signedPayload)
    }, serverUrl);
  }
};

export const deviceEnrollmentAPI = {
  create: async (
    payload: CreateDeviceEnrollmentRequest,
    serverUrl?: string
  ): Promise<ApiResponse<CreateDeviceEnrollmentResponse>> => {
    return apiClient.apiRequest('/device-enrollments', {
      method: 'POST',
      body: JSON.stringify(payload)
    }, serverUrl);
  },

  readRequest: async (
    enrollmentId: string,
    signedPayload: SignedRequest<ReadDeviceEnrollmentRequest>,
    serverUrl?: string
  ): Promise<ApiResponse<ReadDeviceEnrollmentResponse>> => {
    return apiClient.signedRequest(`/device-enrollments/${encodeURIComponent(enrollmentId)}/read-request`, signedPayload, serverUrl);
  },

  complete: async (
    enrollmentId: string,
    signedPayload: SignedRequest<CompleteDeviceEnrollmentRequest>,
    serverUrl?: string
  ): Promise<ApiResponse<CompleteDeviceEnrollmentResponse>> => {
    return apiClient.signedRequest(`/device-enrollments/${encodeURIComponent(enrollmentId)}/complete`, signedPayload, serverUrl);
  },

  readPayload: async (
    enrollmentId: string,
    signedPayload: SignedRequest<{ enrollmentId: string }>,
    serverUrl?: string
  ): Promise<ApiResponse<ReadDeviceEnrollmentPayloadResponse>> => {
    return apiClient.apiRequest(`/device-enrollments/${encodeURIComponent(enrollmentId)}/payload`, {
      method: 'POST',
      body: JSON.stringify(signedPayload)
    }, serverUrl);
  },

  reject: async (
    enrollmentId: string,
    signedPayload: SignedRequest<{ trustedDeviceId: string }>,
    serverUrl?: string
  ): Promise<ApiResponse<{ enrollmentId: string; state: 'rejected' }>> => {
    return apiClient.signedRequest(`/device-enrollments/${encodeURIComponent(enrollmentId)}/reject`, signedPayload, serverUrl);
  }
};

export const temporaryAccessAPI = {
  createTrustedAccessRequest: async (
    signedPayload: SignedRequest<CreateTemporaryTrustedAccessRequestRequest>,
    serverUrl?: string
  ): Promise<ApiResponse<CreateTemporaryTrustedAccessRequestResponse>> => {
    return apiClient.signedRequest('/temporary-access/trusted/request', signedPayload, serverUrl);
  },

  listTrustedAccessRequests: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<ListTemporaryTrustedAccessRequestsResponse>> => {
    return apiClient.signedRequest('/temporary-access/trusted/requests', signedPayload, serverUrl);
  },

  approveTrustedAccessRequest: async (
    signedPayload: SignedRequest<ApproveTemporaryTrustedAccessRequestRequest>,
    serverUrl?: string
  ): Promise<ApiResponse<CreateTemporaryTrustedAccessRequestResponse>> => {
    return apiClient.signedRequest('/temporary-access/trusted/approve', signedPayload, serverUrl);
  },

  rejectTrustedAccessRequest: async (
    signedPayload: SignedRequest<RejectTemporaryTrustedAccessRequestRequest>,
    serverUrl?: string
  ): Promise<ApiResponse<CreateTemporaryTrustedAccessRequestResponse>> => {
    return apiClient.signedRequest('/temporary-access/trusted/reject', signedPayload, serverUrl);
  },

  readTrustedAccessPayload: async (
    signedPayload: SignedRequest<{ requestId: string }>,
    serverUrl?: string
  ): Promise<ApiResponse<ReadTemporaryTrustedAccessPayloadResponse>> => {
    return apiClient.signedRequest('/temporary-access/trusted/payload', signedPayload, serverUrl);
  },

  listTemporaryDevices: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{
    devices: Array<{
      deviceId: string;
      identityId: string;
      publicKey: PublicKey;
      encryptionPublicKey?: PublicKey | null;
      approvedByDeviceId: string;
      createdAt: string;
      lastSeenAt: string | null;
      expiresAt: string;
      status: 'active' | 'revoked' | 'expired';
    }>;
  }>> => {
    return apiClient.signedRequest('/temporary-access/devices/list', signedPayload, serverUrl);
  },

  revokeTemporaryDevice: async (
    signedPayload: SignedRequest<{ deviceId: string }>,
    serverUrl?: string
  ): Promise<ApiResponse<{ deviceId: string; status: 'revoked' }>> => {
    return apiClient.signedRequest('/temporary-access/devices/revoke', signedPayload, serverUrl);
  },

  fetchChatKeys: async (
    signedPayload: SignedRequest<{ groupChatIds?: string[]; directChatIds?: string[] }>,
    serverUrl?: string
  ): Promise<ApiResponse<{
    groupChats: Array<{ chatId: string; chatTitle: string; epoch: number; envelopeCiphertext: string; publisherIdentityId: string; publisherEncPublicKey: { algorithm: string; value: string } }>;
    directChats: Array<{ directChatId: string; epoch: number; envelopeCiphertext: string; publisherIdentityId: string; publisherEncPublicKey: { algorithm: string; value: string } }>;
  }>> => {
    return apiClient.signedRequest('/temporary-access/chat-keys', signedPayload, serverUrl);
  },

  fetchApprovedTempDevicesForChat: async (
    signedPayload: SignedRequest<{ chatId: string; chatType?: 'group' | 'direct' }>,
    serverUrl?: string
  ): Promise<ApiResponse<{
    devices: Array<{
      deviceId: string;
      encryptionPublicKey: { algorithm: string; value: string } | null;
      approvalAttestation: {
        payload: string;
        signature: string;
        approvingDevicePublicKey: string;
      } | null;
    }>;
  }>> => {
    return apiClient.signedRequest('/temporary-access/chat-keys/approved-devices', signedPayload, serverUrl);
  },

  updateTempDeviceChatKeys: async (
    signedPayload: SignedRequest<{
      chatId: string;
      chatType?: 'group' | 'direct';
      epoch: number;
      envelopes: Array<{ temporaryDeviceId: string; envelopeCiphertext: string; publisherEncPublicKeyAlgo: string; publisherEncPublicKeyValue: string }>;
    }>,
    serverUrl?: string
  ): Promise<ApiResponse<{ stored: number }>> => {
    return apiClient.signedRequest('/temporary-access/chat-keys/update', signedPayload, serverUrl);
  }
};

/**
 * Vault API
 */
export const vaultAPI = {
  /**
   * Get vault (signed request)
   */
  get: async <P>(
    signedPayload: SignedRequest<P>,
    serverUrl?: string
  ): Promise<ApiResponse<GetVaultResponse>> => {
    return apiClient.signedRequest('/vault/get', signedPayload, serverUrl);
  },

  /**
   * Set vault (signed request)
   */
  set: async (
    signedPayload: SignedRequest<SetVaultPayload>,
    serverUrl?: string
  ): Promise<ApiResponse<{ revision: number; updatedAt: string }>> => {
    return apiClient.signedRequest('/vault/set', signedPayload, serverUrl);
  }
};

/**
 * Config API
 */
export const configAPI = {
  /**
   * Get server capabilities. Older family servers do not expose this endpoint;
   * a 404 is treated as a legacy server with conservative feature flags.
   */
  getCapabilities: async (serverUrl?: string): Promise<ApiResponse<ServerCapabilitiesResponse>> => {
    try {
      const response = await apiClient.apiRequest<ServerCapabilitiesResponse>(
        '/config/capabilities',
        { method: 'GET' },
        serverUrl
      );
      if (response.status === 'ok' && response.result) {
        return response;
      }
      return response;
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        return {
          status: 'ok',
          result: legacyServerCapabilities()
        };
      }
      throw error;
    }
  },

  /**
   * Get ICE servers configuration
   */
  getIceServers: async (serverUrl?: string): Promise<ApiResponse<GetIceServersResponse>> => {
    return apiClient.apiRequest(
      '/config/ice-servers',
      {
        method: 'GET'
      },
      serverUrl
    );
  },

  /**
   * Get TURN credentials (should be requested per call)
   */
  getTurnCredentials: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<GetTurnCredentialsResponse>> => {
    return apiClient.signedRequest('/config/turn-credentials', signedPayload, serverUrl);
  },

  /**
   * Get server name (device-signed; only after device registration)
   */
  getServerName: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{ serverName: string; noNamesOnServer?: boolean; ownerIdentityId?: string | null; ownerIdentityName?: string | null }>> => {
    return apiClient.signedRequest('/config/server-name', signedPayload, serverUrl);
  }
};

function legacyServerCapabilities(): ServerCapabilitiesResponse {
  return {
    schemaVersion: 0,
    serverVersion: 'legacy',
    api: {
      minClientVersion: '0.1.0',
      recommendedClientVersion: 'legacy'
    },
    features: {
      familyDomains: false,
      domainMigrationNotice: false,
      deviceEnrollmentV2: false,
      temporaryTrustedAccess: false,
      attachments: false,
      avatarStorage: false,
      callHistorySync: false,
      mobilePushCentral: false,
      groupChats: true,
      groupChatInviteLinks: false,
      directGuestLinks: false,
      messageMutations: false,
      serverAdminTenants: false,
      tenantOwnerClaims: false
    },
    limits: {
      messageTtlHours: 24,
      attachmentsEnabled: false,
      maxAttachmentFileSizeBytes: null,
      attachmentStorageQuotaBytes: null,
      attachmentRetentionSeconds: null
    },
    canonical: {
      publicBaseUrl: null,
      acceptedDomainRole: null,
      acceptedDomainStatus: null
    }
  };
}

/**
 * Status API
 */
export const statusAPI = {
  /**
   * Set own status (signed request)
   */
  set: async (
    signedPayload: SignedRequest<{ statusText: string | null }>,
    serverUrl?: string
  ): Promise<ApiResponse<{ statusText: string | null; statusUpdatedAt: string }>> => {
    return apiClient.signedRequest('/status/set', signedPayload, serverUrl);
  },

  /**
   * Get statuses for multiple identities (signed request)
   */
  get: async (
    signedPayload: SignedRequest<{ identityIds: string[] }>,
    serverUrl?: string
  ): Promise<ApiResponse<{
    statuses: Record<string, {
      statusText: string | null;
      statusUpdatedAt: string | null;
      avatarBlobId?: string | null;
      presence?: {
        visibility?: 'visible' | 'hidden';
        isOnline: boolean;
        lastSeenAt: string | null;
        onlineUntil: string | null;
      };
    }>
  }>> => {
    return apiClient.signedRequest('/status/get', signedPayload, serverUrl);
  },

  heartbeat: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{ at: string }>> => {
    return apiClient.signedRequest('/status/heartbeat', signedPayload, serverUrl);
  },

  /**
   * Get own status (signed request)
   */
  my: async <P>(
    signedPayload: SignedRequest<P>,
    serverUrl?: string
  ): Promise<ApiResponse<{ statusText: string | null; statusUpdatedAt: string | null }>> => {
    return apiClient.signedRequest('/status/my', signedPayload, serverUrl);
  }
};

/**
 * Identities API
 */
export const identitiesAPI = {
  deleteSelf: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{ deleted: boolean }>> => {
    return apiClient.signedRequest('/identities/delete-self', signedPayload, serverUrl);
  },

  deleteOwnedCircle: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{ deleted: boolean }>> => {
    return apiClient.signedRequest('/identities/delete-owned-circle', signedPayload, serverUrl);
  },

  getSettings: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{
    presenceVisible: boolean;
    identityName: string | null;
    publishIdentity: boolean;
  }>> => {
    return apiClient.signedRequest('/identities/settings', signedPayload, serverUrl);
  },

  updatePresenceVisibility: async (
    signedPayload: SignedRequest<{ presenceVisible: boolean }>,
    serverUrl?: string
  ): Promise<ApiResponse<{ presenceVisible: boolean }>> => {
    return apiClient.signedRequest('/identities/presence-visibility', signedPayload, serverUrl);
  },

  /**
   * Get published identities for a server (device-signed)
   */
  listPublished: async (
    signedPayload: SignedRequest<Record<string, never>>,
    serverUrl?: string
  ): Promise<ApiResponse<{ identities: Array<{ identityId: string; publicKey: PublicKey; identityName: string | null }> }>> => {
    return apiClient.signedRequest('/identities/published', signedPayload, serverUrl);
  }
};

/**
 * Devices API
 */
export const devicesAPI = {
  /**
   * List devices for current identity (signed request)
   */
  list: async <P>(
    signedPayload: SignedRequest<P>,
    serverUrl?: string
  ): Promise<ApiResponse<{
    devices: Array<{
      deviceId: string;
      publicKey: PublicKey;
      encryptionPublicKey?: PublicKey | null;
      registrationAttestation?: DeviceRegistrationAttestation | null;
      label?: string | null;
      createdAt: string;
      lastSeenAt: string | null;
      status: 'active' | 'revoked';
    }>;
    currentDeviceId: string;
  }>> => {
    return apiClient.signedRequest('/devices/list', signedPayload, serverUrl);
  },

  /**
   * Revoke device (signed request)
   */
  revoke: async (
    signedPayload: SignedRequest<RevokeDevicePayload>,
    serverUrl?: string
  ): Promise<ApiResponse<{ deviceId: string; status: 'revoked'; localDeletionRequested?: boolean }>> => {
    return apiClient.signedRequest('/devices/revoke', signedPayload, serverUrl);
  }
};

/**
 * Backup API
 */
export const backupAPI = {
  /**
   * Upload or update the device's encrypted backup blob (signed request).
   */
  set: async (
    signedPayload: SignedRequest<{ encryptedBackup: EncryptedBlob }>,
    serverUrl?: string
  ): Promise<ApiResponse<{ version: number; updatedAt: string }>> => {
    return apiClient.signedRequest('/backup/set', signedPayload, serverUrl);
  },

  /**
   * Fetch all encrypted backup blobs for an identity by public key.
   * Unauthenticated — no signature required.
   */
  list: async (
    identityPublicKey: PublicKey,
    serverUrl?: string
  ): Promise<ApiResponse<{ backups: Array<{ encryptedBackup: EncryptedBlob; version: number }> }>> => {
    return apiClient.apiRequest(
      '/backup/list',
      { method: 'POST', body: JSON.stringify({ identityPublicKey }) },
      serverUrl
    );
  }
};
